import { Suspense, useState, useEffect, useRef } from "react";
import reportsServices from '../../services/reports/reportservice';
import AppLoading from '../app-loading/app-loading';
import { Toast } from "primereact/toast";
import { InputText } from 'primereact/inputtext';
import { Dropdown } from 'primereact/dropdown';
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import ReportPageHeader from '../shared/ReportPageHeader';

export const TreemapChart = () => {
    const toast = useRef<Toast>(null);
    const [loading, setLoading] = useState(false);

    const today = new Date();

    const [year, setYear] = useState<any>(today.getFullYear());
    const [currentMonth] = useState<any>(today.getMonth() + 1);

    const rawRemainingDays =
        new Date(year, currentMonth, 0).getDate() - today.getDate();

    const remainingDays = rawRemainingDays <= 0 ? 1 : rawRemainingDays;

    const [responseData, setResponseData] = useState<any[]>([]);

    const [rawTotals, setRawTotals] = useState({
        target: 0,
        targetReached: 0,
        remainingTargetPerDay: 0,
        targetReachedPercentage: 0.0
    });

    const monthNames = [
        { id: 1, month: "January" },
        { id: 2, month: "February" },
        { id: 3, month: "March" },
        { id: 4, month: "April" },
        { id: 5, month: "May" },
        { id: 6, month: "June" },
        { id: 7, month: "July" },
        { id: 8, month: "August" },
        { id: 9, month: "September" },
        { id: 10, month: "October" },
        { id: 11, month: "November" },
        { id: 12, month: "December" }
    ];

    const [selectedMonth, setSelectedMonth] = useState<any>(
        monthNames.filter(x => x.id === currentMonth)[0]
    );

    type POSNode = {
        name: string;
        size: string;
        color?: string;
        targetReached: string;
        target: string;
        month: string;
    };

    const nFormatter = (amount: number): string => {
        return (Number(amount || 0) / 100000)
            .toFixed(2)
            .replace(/\.00$/, '') + 'L';
    };

    const formatPercentage = (value: number): string => {
        return Number.isFinite(value)
            ? value.toFixed(2) + '%'
            : '0.00%';
    };

    /*
     * The UI displays values in lakhs rounded to 2 decimals.
     *
     * ₹240   = 0.0024L -> displays as 0L
     * ₹30    = 0.0003L -> displays as 0L
     *
     * Treat targets below ₹1,000 as not configured for percentage purposes.
     * Change this value if your business has a different minimum target.
     */
    const minimumMeaningfulTarget = 1000;

    const FeatchData = async (monthId = selectedMonth.id) => {
        setLoading(true);

        const filterData: POSNode[] = [];

        let totalTarget = 0;
        let totalReached = 0;
        let totalRemainingPerDay = 0;

        try {
            const respones: any[] =
                await reportsServices.getPOSTargetMappingMonthList({
                    month: monthId,
                    year: year
                });

            if (Array.isArray(respones) && respones.length > 0) {
                for (let index = 0; index < respones.length; index++) {
                    const res = respones[index];

                    const target = Number(res.target ?? 0);
                    const invoiceAmount = Number(res.invoiceAmount ?? 0);

                    /*
                     * A target below minimumMeaningfulTarget displays as 0L.
                     * So show 0% rather than treating it as a valid target.
                     */
                    const hasValidTarget = target >= minimumMeaningfulTarget;
                    const hasAchievement = invoiceAmount > 0;

                    const remainingTargetPerMonth =
                        hasValidTarget
                            ? Math.max(0, target - invoiceAmount) / remainingDays
                            : 0;

                    const targetAchievedPercentage =
                        !hasValidTarget || !hasAchievement
                            ? 0
                            : Math.min(
                                Math.round((invoiceAmount / target) * 100),
                                100
                            );

                    totalTarget += target;
                    totalReached += invoiceAmount;

                    totalRemainingPerDay += currentMonth === selectedMonth.id
                        ? remainingTargetPerMonth
                        : 0;

                    const objdata: POSNode = {
                        name: res.pos ?? '',
                        size: `${targetAchievedPercentage}%`,
                        color: res.color,
                        targetReached: nFormatter(invoiceAmount),
                        target: nFormatter(target),
                        month: currentMonth === selectedMonth.id
                            ? nFormatter(remainingTargetPerMonth)
                            : nFormatter(0)
                    };

                    filterData.push(objdata);
                }
            }

            /*
             * The total still uses actual target values.
             * If you also want to exclude targets under ₹1,000 from the total
             * percentage calculation, use a separate validTarget total.
             */
            const totalTargetReachedPercentage =
                totalTarget <= 0 || totalReached <= 0
                    ? 0
                    : Math.min(
                        (totalReached / totalTarget) * 100,
                        100
                    );

            setResponseData(filterData);

            setRawTotals({
                target: totalTarget,
                targetReached: totalReached,
                remainingTargetPerDay: totalRemainingPerDay,
                targetReachedPercentage: totalTargetReachedPercentage
            });
        } catch (error) {
            console.error("Unable to load POS performance report:", error);

            setResponseData([]);

            setRawTotals({
                target: 0,
                targetReached: 0,
                remainingTargetPerDay: 0,
                targetReachedPercentage: 0
            });

            toast.current?.show({
                severity: "error",
                summary: "Error",
                detail: "Unable to load POS performance report.",
                life: 3000
            });
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        FeatchData(selectedMonth.id);
    }, [selectedMonth]);

    return (
        <Suspense fallback={<AppLoading />}>
            <Toast ref={toast} />

            <div className="layout-content mt-3 mb-3 grid grid-table-mobile">
                <div className="col-12">
                    <ReportPageHeader
                        icon="pi-chart-pie"
                        title="POS Performance Report"
                        subtitle="Track monthly POS target achievement and daily remaining targets"
                        filterLabel="Filter Performance Report"
                    >
                        <div className="grid">
                            <div className="col-12 sm:col-6 lg:col-4">
                                <InputText
                                    id="Year"
                                    name="Year"
                                    keyfilter="int"
                                    value={year}
                                    onChange={(e) => setYear(e.target.value)}
                                    placeholder={year + "#"}
                                    style={{ width: "100%" }}
                                    disabled
                                />
                            </div>

                            <div className="col-12 sm:col-6 lg:col-4">
                                <Dropdown
                                    value={selectedMonth}
                                    onChange={(e) => {
                                        setSelectedMonth(e.value);
                                    }}
                                    options={monthNames}
                                    optionLabel="month"
                                    placeholder="Select a Month"
                                    className="w-full"
                                />
                            </div>
                        </div>
                    </ReportPageHeader>
                </div>

                <div className="col-12 card border-noround p-2">
                    <div className="flex justify-content-start mb-2">
                        <div
                            className="flex align-items-center gap-2 p-2 border-round"
                            style={{
                                backgroundColor: 'var(--blue-50)',
                                border: '1px solid var(--blue-100)',
                                display: 'inline-flex',
                                maxWidth: 'fit-content'
                            }}
                        >
                            <i
                                className="pi pi-info-circle"
                                style={{
                                    color: 'var(--blue-600)',
                                    fontSize: '1.25rem'
                                }}
                            />
                            <span style={{ color: 'var(--blue-700)' }}>
                                All amounts are exclusive of taxes.
                            </span>
                        </div>
                    </div>

                    <div className="flex justify-content-start mb-2">
                        <div
                            className="flex align-items-center gap-2 p-2 border-round"
                            style={{
                                backgroundColor: 'var(--green-50)',
                                border: '1px solid var(--green-100)',
                                display: 'inline-flex',
                                maxWidth: 'fit-content'
                            }}
                        >
                            <i
                                className="pi pi-info-circle"
                                style={{
                                    color: 'var(--green-600)',
                                    fontSize: '1.25rem'
                                }}
                            />
                            <span style={{ color: 'var(--green-700)' }}>
                                Note: Go to POS Target Mapping and set the monthly target for each POS.
                            </span>
                        </div>
                    </div>

                    {responseData !== null && (
                        <>
                            <style>{`
                                .pos-report-table .p-datatable-tbody > tr.pos-report-row-gray > td {
                                    background: #f3f3f3 !important;
                                }
                                .pos-report-table .p-datatable-tbody > tr.pos-report-row-white > td {
                                    background: #ffffff !important;
                                }
                                .pos-report-table .p-datatable-tfoot > tr > td {
                                    background: transparent !important;
                                }
                            `}</style>

                            <DataTable
                                className="pos-report-table"
                                editMode="row"
                                value={responseData}
                                paginator
                                rows={10}
                                rowsPerPageOptions={[5, 10, 25, 50, 100]}
                                stripedRows
                                showGridlines
                                rowClassName={(rowData, rowIndex) => {
                                    const index = Number(rowIndex ?? 0);
                                    return index % 2 === 0 ? 'pos-report-row-gray' : 'pos-report-row-white';
                                }}
                                emptyMessage="No order found."
                                paginatorTemplate="RowsPerPageDropdown FirstPageLink PrevPageLink CurrentPageReport NextPageLink LastPageLink"
                                currentPageReportTemplate="{first} to {last} of {totalRecords}"
                            >
                            <Column
                                field="name"
                                header="Name"
                                style={{ width: '20%', fontSize: "1.3rem" }}
                                footer="Total:"
                                footerStyle={{ background: 'transparent' }}
                            />

                            <Column
                                field="target"
                                header="Target Per Month"
                                style={{
                                    width: '20%',
                                    fontSize: "1.3rem",
                                    textAlign: "center"
                                }}
                                footer={nFormatter(rawTotals.target)}
                                footerStyle={{ background: 'transparent' }}
                            />

                            <Column
                                field="targetReached"
                                header={
                                    selectedMonth.id === currentMonth
                                        ? "Target Achieved (as on " +
                                          new Date(today).toLocaleDateString("en-gb", {
                                              day: "2-digit",
                                              month: "short",
                                              year: "numeric"
                                          }) +
                                          ")"
                                        : "Target Achieved"
                                }
                                style={{
                                    width: '20%',
                                    fontSize: "1.3rem",
                                    textAlign: "center"
                                }}
                                footer={nFormatter(rawTotals.targetReached)}
                                footerStyle={{ background: 'transparent' }}
                            />

                            <Column
                                field="month"
                                header={
                                    selectedMonth.id === currentMonth
                                        ? "Target Per Day For Remaining (" +
                                          rawRemainingDays +
                                          ") Days"
                                        : "Target Per Day For Remaining Days"
                                }
                                style={{
                                    width: '20%',
                                    fontSize: "1.3rem",
                                    textAlign: "center"
                                }}
                                footer={nFormatter(rawTotals.remainingTargetPerDay)}
                                footerStyle={{ background: 'transparent' }}
                            />

                            <Column
                                field="size"
                                header="Target Achieved (%)"
                                style={{
                                    width: '20%',
                                    fontSize: "1.3rem",
                                    textAlign: "center"
                                }}
                                footer={formatPercentage(rawTotals.targetReachedPercentage)}
                                footerStyle={{ background: 'transparent' }}
                            />
                        </DataTable>
                        </>
                    )}
                </div>
            </div>

            {loading && <AppLoading />}
        </Suspense>
    );
};